import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, ImageIcon, Loader2, X } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import {
  chaoUcStagePhotoFileUrl,
  deleteChaoUcStagePhoto,
  emptyChaoUcStageSchedule,
  fetchChaoUcStageSchedules,
  saveChaoUcStageDates,
  uploadChaoUcStagePhoto,
  type ChaoUcStagePhotoDto,
  type ChaoUcStagePhotoKind,
  type ChaoUcStageScheduleDto,
} from "@/services/chaoDeObra";
import { cn } from "@/utils/cn";

interface Props {
  open: boolean;
  onClose: () => void;
  costCenterId: string;
  ucLabel: string;
  canEdit?: boolean;
  onSaved: (schedule: ChaoUcStageScheduleDto) => void;
}

function PhotoLightbox({
  open,
  src,
  title,
  onClose,
}: {
  open: boolean;
  src: string;
  title: string;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 px-4 py-8"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white hover:bg-white/25"
        aria-label="Fechar foto"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={src}
        alt={title}
        className="max-h-[88vh] max-w-[min(96vw,1100px)] rounded-lg object-contain shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      />
    </div>,
    document.body,
  );
}

function PhotoSlot({
  label,
  photo,
  busy,
  disabled,
  canEdit,
  onPick,
  onOpen,
  onRemove,
}: {
  label: string;
  photo: ChaoUcStagePhotoDto | null;
  busy: boolean;
  disabled: boolean;
  canEdit: boolean;
  onPick: (file: File) => void;
  onOpen: () => void;
  onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className="space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        disabled={!canEdit || disabled || busy}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onPick(file);
        }}
      />
      {photo ? (
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2">
          <button
            type="button"
            onClick={onOpen}
            title="Ver foto"
            aria-label={`Ver ${label.toLowerCase()}`}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-blue-600 shadow-sm ring-1 ring-slate-200 hover:bg-blue-50"
          >
            <ImageIcon className="h-4 w-4" />
          </button>
          <p className="min-w-0 flex-1 truncate text-xs text-slate-600" title={photo.originalName}>
            {photo.originalName || "Foto anexada"}
          </p>
          {canEdit ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled || busy}
                onClick={() => inputRef.current?.click()}
              >
                Trocar
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled || busy}
                onClick={onRemove}
              >
                Remover
              </Button>
            </>
          ) : null}
        </div>
      ) : canEdit ? (
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed px-3 py-3 text-xs font-semibold transition-colors",
            disabled || busy
              ? "cursor-not-allowed border-slate-200 text-slate-400"
              : "border-blue-200 text-blue-700 hover:bg-blue-50",
          )}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          {busy ? "Enviando…" : "Adicionar foto"}
        </button>
      ) : (
        <p className="rounded-xl border border-dashed border-slate-200 px-3 py-3 text-center text-xs text-slate-400">
          Sem foto
        </p>
      )}
    </div>
  );
}

export function ChaoUcEtapaDatasModal({
  open,
  onClose,
  costCenterId,
  ucLabel,
  canEdit = false,
  onSaved,
}: Props) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startPhoto, setStartPhoto] = useState<ChaoUcStagePhotoDto | null>(null);
  const [endPhoto, setEndPhoto] = useState<ChaoUcStagePhotoDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState<ChaoUcStagePhotoKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<{ src: string; title: string } | null>(null);

  const applySchedule = (s: ChaoUcStageScheduleDto) => {
    setStartDate(s.startDate ?? "");
    setEndDate(s.endDate ?? "");
    setStartPhoto(s.startPhoto);
    setEndPhoto(s.endPhoto);
  };

  const load = useCallback(async () => {
    if (!costCenterId || !ucLabel) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchChaoUcStageSchedules(costCenterId);
      if (!res.success) {
        setError(res.error ?? "Não foi possível carregar as datas.");
        applySchedule(emptyChaoUcStageSchedule());
        return;
      }
      applySchedule(res.schedules?.[ucLabel] ?? emptyChaoUcStageSchedule());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível carregar as datas.");
      applySchedule(emptyChaoUcStageSchedule());
    } finally {
      setLoading(false);
    }
  }, [costCenterId, ucLabel]);

  useEffect(() => {
    if (!open) {
      setViewer(null);
      setError(null);
      return;
    }
    void load();
  }, [open, load]);

  const dateError =
    startDate && endDate && endDate < startDate
      ? "A data final deve ser igual ou posterior à data de início."
      : undefined;

  const persistSchedule = (s: ChaoUcStageScheduleDto) => {
    applySchedule(s);
    onSaved(s);
  };

  const onSave = async () => {
    if (!canEdit) return;
    if (dateError) {
      setError(dateError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await saveChaoUcStageDates(
        costCenterId,
        ucLabel,
        startDate || null,
        endDate || null,
      );
      if (!res.success || !res.schedule) {
        setError(res.error ?? "Falha ao salvar.");
        return;
      }
      persistSchedule(res.schedule);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const onPickPhoto = async (kind: ChaoUcStagePhotoKind, file: File) => {
    if (!canEdit) return;
    setPhotoBusy(kind);
    setError(null);
    try {
      const res = await uploadChaoUcStagePhoto(costCenterId, ucLabel, kind, file);
      if (!res.success || !res.schedule) {
        setError(res.error ?? "Falha ao enviar a foto.");
        return;
      }
      persistSchedule(res.schedule);
    } finally {
      setPhotoBusy(null);
    }
  };

  const onRemovePhoto = async (kind: ChaoUcStagePhotoKind) => {
    if (!canEdit) return;
    setPhotoBusy(kind);
    setError(null);
    try {
      const res = await deleteChaoUcStagePhoto(costCenterId, ucLabel, kind);
      if (!res.success || !res.schedule) {
        setError(res.error ?? "Falha ao remover a foto.");
        return;
      }
      persistSchedule(res.schedule);
    } finally {
      setPhotoBusy(null);
    }
  };

  const openPhoto = (photo: ChaoUcStagePhotoDto | null, title: string) => {
    if (!photo) return;
    setViewer({ src: chaoUcStagePhotoFileUrl(photo.id), title });
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Datas da etapa"
        size="md"
        closeOnBackdropClick={!viewer}
        closeOnEscape={!viewer}
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Fechar
            </Button>
            {canEdit ? (
              <Button type="button" onClick={() => void onSave()} loading={saving} disabled={loading || !!dateError}>
                Salvar
              </Button>
            ) : null}
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm font-semibold text-slate-800">{ucLabel}</p>
          <p className="text-xs text-slate-500">
            {canEdit
              ? "Informe o início e o fim desta unidade construtiva. As fotos são salvas ao anexar; as datas entram ao salvar."
              : "Somente administradores podem alterar as datas e as fotos desta etapa."}
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando…
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                <DateInput
                  label="Data de início"
                  value={startDate}
                  onChange={setStartDate}
                  max={endDate || undefined}
                  disabled={!canEdit || saving}
                />
                <PhotoSlot
                  label="Foto do início"
                  photo={startPhoto}
                  busy={photoBusy === "start"}
                  disabled={saving || !costCenterId}
                  canEdit={canEdit}
                  onPick={(file) => void onPickPhoto("start", file)}
                  onOpen={() => openPhoto(startPhoto, `Início — ${ucLabel}`)}
                  onRemove={() => void onRemovePhoto("start")}
                />
              </div>
              <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                <DateInput
                  label="Data final"
                  value={endDate}
                  onChange={setEndDate}
                  min={startDate || undefined}
                  error={canEdit ? dateError : undefined}
                  disabled={!canEdit || saving}
                />
                <PhotoSlot
                  label="Foto do final"
                  photo={endPhoto}
                  busy={photoBusy === "end"}
                  disabled={saving || !costCenterId}
                  canEdit={canEdit}
                  onPick={(file) => void onPickPhoto("end", file)}
                  onOpen={() => openPhoto(endPhoto, `Final — ${ucLabel}`)}
                  onRemove={() => void onRemovePhoto("end")}
                />
              </div>
            </div>
          )}

          {error && <p className="text-xs font-medium text-red-600">{error}</p>}
        </div>
      </Modal>

      <PhotoLightbox
        open={!!viewer}
        src={viewer?.src ?? ""}
        title={viewer?.title ?? "Foto da etapa"}
        onClose={() => setViewer(null)}
      />
    </>
  );
}
